import { Palette } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { ProposalStyle } from "@/types/proposal";

/** Today's standard look; a new design starts from it */
export const STANDARD_STYLE: ProposalStyle = {
  font: "sans",
  colors: {
    accent: "#312e81",
    secondary: "#1f7a5c",
    text: "#1e293b",
    muted: "#64748b",
    border: "#e2e8f0",
    heading: "#312e81",
    table_head_bg: "#312e81",
    table_head_text: "#ffffff",
    zebra: "#f8fafc",
    note_bg: "#fef7e0",
    note_border: "#e8d9a8",
    note_text: "#a16207",
    total_bg: "#e3f1ea",
  },
  sizes: { base: 10, heading: 13, cover_title: 26 },
  heading: "plain",
  numbered_headings: true,
  cover: { layout: "left" },
  table: "grid",
  page_numbers: "bottom_right",
};

/** Ready colour sets: the main colour drives titles, lines and the table header */
const PALETTES: Array<{ name: string; accent: string; secondary: string; zebra: string; total: string }> = [
  { name: "Indigo", accent: "#312e81", secondary: "#1f7a5c", zebra: "#f8fafc", total: "#e3f1ea" },
  { name: "Teal", accent: "#0f766e", secondary: "#b45309", zebra: "#f0fdfa", total: "#ccfbf1" },
  { name: "Maroon", accent: "#7f1d1d", secondary: "#a16207", zebra: "#fef2f2", total: "#fee2e2" },
  { name: "Forest", accent: "#166534", secondary: "#1e40af", zebra: "#f0fdf4", total: "#dcfce7" },
  { name: "Ocean", accent: "#1e40af", secondary: "#0e7490", zebra: "#eff6ff", total: "#dbeafe" },
  { name: "Charcoal", accent: "#27272a", secondary: "#b45309", zebra: "#f4f4f5", total: "#e4e4e7" },
];

const COLOR_LABELS: Array<[keyof ProposalStyle["colors"], string]> = [
  ["accent", "Main colour"],
  ["heading", "Section titles"],
  ["text", "Body text"],
  ["muted", "Small labels"],
  ["secondary", "Second colour"],
  ["table_head_bg", "Table header"],
  ["table_head_text", "Table header text"],
  ["zebra", "Shaded rows"],
  ["total_bg", "Total row"],
  ["border", "Lines"],
  ["note_bg", "Note box"],
  ["note_text", "Note box text"],
];

function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium">{label}</div>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
        {options.map(([v, text]) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={value === v}
            onClick={() => onChange(v)}
            className={cn("rounded-md border px-2.5 py-1 text-xs transition-colors", value === v ? "border-primary bg-primary/10 font-semibold text-primary" : "hover:bg-muted/60")}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Size({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void }) {
  return (
    <label className="block space-y-1">
      <span className="flex justify-between text-xs font-medium">
        {label} <span className="text-muted-foreground tabular-nums">{value} pt</span>
      </span>
      <input type="range" min={min} max={max} step={0.5} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-primary" />
    </label>
  );
}

/**
 * A template's design: fonts, colours and how titles, the cover text and price tables look. Off means the standard look.
 * The page is never painted: it is the company letterhead when one is set up, or plain white.
 */
export function TemplateDesignEditor({ style, onChange }: { style: ProposalStyle | null; onChange: (next: ProposalStyle | null) => void }) {
  const st = style;
  const set = (patch: Partial<ProposalStyle>) => st && onChange({ ...st, ...patch });
  const setColor = (k: keyof ProposalStyle["colors"], v: string) => st && onChange({ ...st, colors: { ...st.colors, [k]: v } });
  return (
    <div className="bg-card border rounded-2xl p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-bold">
            <Palette className="size-4 text-primary" /> Design
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pages print on your letterhead when one is set up (Settings → Company), otherwise on a plain white page. The design styles the text, titles and tables on it.
          </p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-xs font-medium">
          <Switch checked={!!st} onCheckedChange={(on) => onChange(on ? STANDARD_STYLE : null)} aria-label="Use its own design" />
          Own design
        </label>
      </div>

      {!st ? (
        <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">Uses the standard look. Turn on "Own design" to choose fonts and colours.</p>
      ) : (
        <>
          <div className="space-y-1">
            <div className="text-xs font-medium">Colour sets</div>
            <div className="flex flex-wrap gap-1.5">
              {PALETTES.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => onChange({ ...st, colors: { ...st.colors, accent: p.accent, heading: p.accent, table_head_bg: p.accent, secondary: p.secondary, zebra: p.zebra, total_bg: p.total } })}
                  className={cn("flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-muted/60", st.colors.accent === p.accent && "border-primary")}
                >
                  <span className="size-3 rounded-full" style={{ background: p.accent }} />
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
            {COLOR_LABELS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-xs">
                <span className="relative size-6 shrink-0 cursor-pointer rounded-md border" style={{ background: st.colors[k] }}>
                  <input type="color" value={st.colors[k]} onChange={(e) => setColor(k, e.target.value)} aria-label={label} className="absolute inset-0 size-full cursor-pointer opacity-0" />
                </span>
                <span className="truncate">{label}</span>
              </label>
            ))}
          </div>

          <Choice label="Font" value={st.font} options={[["sans", "Clean"], ["serif", "Classic"], ["modern", "Modern"]]} onChange={(font) => set({ font })} />
          <Choice label="Section titles" value={st.heading} options={[["plain", "Plain"], ["underline", "Underlined"], ["band", "Light strip"], ["left_bar", "Bar on the left"]]} onChange={(heading) => set({ heading })} />
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={st.numbered_headings} onCheckedChange={(numbered_headings) => set({ numbered_headings })} aria-label="Number the section titles" /> Number the section titles (1. 2. 3.)
          </label>
          <Choice label="Cover title" value={st.cover.layout} options={[["left", "On the left"], ["centered", "Centred"]]} onChange={(layout) => set({ cover: { ...st.cover, layout } })} />
          <label className="block space-y-1">
            <span className="text-xs font-medium">Small word above the title</span>
            <input
              value={st.cover.subtitle || ""}
              maxLength={80}
              placeholder="Proposal"
              onChange={(e) => set({ cover: { ...st.cover, subtitle: e.target.value } })}
              className="h-8 w-full rounded-md border bg-background px-2 text-sm"
            />
          </label>
          <Choice label="Price table" value={st.table} options={[["grid", "All lines"], ["lines", "Row lines"], ["zebra", "Shaded rows"]]} onChange={(table) => set({ table })} />
          <Choice label="Page numbers" value={st.page_numbers} options={[["bottom_right", "Bottom right"], ["bottom_center", "Bottom centre"], ["none", "None"]]} onChange={(page_numbers) => set({ page_numbers })} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Size label="Text" value={st.sizes.base} min={8} max={12} onChange={(base) => set({ sizes: { ...st.sizes, base } })} />
            <Size label="Section titles" value={st.sizes.heading} min={10} max={20} onChange={(heading) => set({ sizes: { ...st.sizes, heading } })} />
            <Size label="Cover title" value={st.sizes.cover_title} min={16} max={40} onChange={(cover_title) => set({ sizes: { ...st.sizes, cover_title } })} />
          </div>
        </>
      )}
    </div>
  );
}
